import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { BRAND } from "../lib/constants";

const IMG_1 = "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?auto=format&fit=crop&w=1400&q=80";
const IMG_2 = "https://images.unsplash.com/photo-1600585154526-990dced4db0d?auto=format&fit=crop&w=1200&q=80";
const IMG_3 = "https://images.unsplash.com/photo-1505691938895-1758d7feb511?auto=format&fit=crop&w=1200&q=80";

export default function About() {
    return (
        <section id="about" className="relative bg-white py-20 md:py-32 overflow-hidden" data-testid="about-section">
            {/* Floating gradient shapes */}
            <div className="absolute -top-20 -left-40 w-[400px] h-[400px] rounded-full bg-[#FF8800] opacity-[0.06] blur-[120px] pointer-events-none" />
            <div className="absolute bottom-0 right-0 w-[450px] h-[450px] rounded-full bg-[#CB1010] opacity-[0.05] blur-[120px] pointer-events-none" />

            <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
                {/* Left: image collage */}
                <div className="lg:col-span-6 relative">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true, margin: "-50px" }}
                        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                        className="relative aspect-[4/5] rounded-[32px] overflow-hidden"
                    >
                        <img
                            src={IMG_1}
                            alt="Luxury residential interior built by Dhanshree Constructions"
                            className="w-full h-full object-cover"
                            loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7, delay: 0.2 }}
                        className="absolute -right-2 md:right-6 top-12 w-32 md:w-48 aspect-[4/5] rounded-2xl overflow-hidden ring-1 ring-black/5"
                    >
                        <img src={IMG_2} alt="Modern home exterior" className="w-full h-full object-cover" loading="lazy" />
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7, delay: 0.35 }}
                        className="absolute -left-2 md:left-8 -bottom-6 w-32 md:w-44 aspect-[4/3] rounded-2xl overflow-hidden ring-1 ring-black/5"
                    >
                        <img src={IMG_3} alt="Architectural planning" className="w-full h-full object-cover" loading="lazy" />
                    </motion.div>

                    {/* Floating badge */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.5 }}
                        className="absolute right-4 md:right-8 -bottom-5 glass rounded-2xl px-4 py-3 ring-1 ring-black/5"
                    >
                        <div className="font-display font-bold text-2xl text-brand-ink leading-none">12+</div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-brand-muted mt-1">Years of craft</div>
                    </motion.div>
                </div>

                {/* Right: content */}
                <div className="lg:col-span-6 lg:pl-8">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="flex items-center gap-3 mb-7"
                    >
                        <span className="w-8 h-px bg-brand-orange" />
                        <span className="text-[11px] uppercase tracking-[0.25em] text-brand-orange font-medium">About Dhanshree</span>
                    </motion.div>

                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="font-display font-semibold text-brand-ink text-3xl md:text-4xl lg:text-[44px] leading-[1.05] tracking-tight"
                        data-testid="about-heading"
                    >
                        Best home builder in Udaipur — building structures that{" "}
                        <span className="font-serif-italic font-normal brand-gradient-text">shape memories.</span>
                    </motion.h2>

                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7, delay: 0.15 }}
                        className="mt-7 text-brand-muted text-base md:text-lg leading-relaxed"
                    >
                        Dhanshree Constructions is one of Udaipur's leading construction
                        companies specializing in modern residential construction, luxury
                        interiors, architectural planning, renovation, and turnkey
                        construction solutions.
                    </motion.p>
                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7, delay: 0.25 }}
                        className="mt-4 text-brand-muted text-base md:text-lg leading-relaxed"
                    >
                        Every project we deliver is a home filled with emotion, comfort,
                        and lifelong memories — handcrafted with premium materials,
                        Vastu-aligned design, and an obsession for quality.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7, delay: 0.35 }}
                        className="mt-10 grid grid-cols-2 gap-px bg-brand-line rounded-2xl overflow-hidden border border-brand-line"
                    >
                        {[
                            { k: "Residential Projects", v: "85+" },
                            { k: "Cities Served", v: "Udaipur" },
                            { k: "On-time Delivery", v: "98%" },
                            { k: "Client Retention", v: "9.6/10" },
                        ].map((s) => (
                            <div key={s.k} className="bg-white p-5">
                                <div className="font-display font-bold text-2xl text-brand-ink">{s.v}</div>
                                <div className="text-xs text-brand-muted mt-1">{s.k}</div>
                            </div>
                        ))}
                    </motion.div>

                    <motion.a
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.45 }}
                        href={BRAND.whatsapp}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-10 group inline-flex items-center gap-3 text-brand-ink font-medium border-b border-brand-ink pb-1 hover:gap-5 transition-all"
                        data-testid="about-cta"
                    >
                        Talk to our founder
                        <ArrowUpRight size={18} className="group-hover:rotate-45 transition-transform" />
                    </motion.a>
                </div>
            </div>
        </section>
    );
}
